/**
 * Shared font catalog + source-aware loader helpers.
 *
 * Single source of truth for which fonts the builder offers, where each one
 * is fetched from (Google Fonts, Fontshare, or system), how their stylesheet
 * URLs are formed, and how to slug-/de-slug-ify family names for the
 * `pb-font-<slug>` inline-font class system.
 *
 * Previously these lived inside `builder/scripts/theme/font-selector.js` (a
 * UI module that also owns combobox plumbing) and a few `https://fonts.…`
 * literals scattered across iframe-renderer.js and font-fetcher.js. Centralized
 * here so runtime CSS, the bridge live-inject path, the export font pipeline,
 * tests, and the property panel can all import a consistent surface.
 *
 * No `store` / `bridge` / DOM imports — this module is pure data + URL
 * formatting so it works in browser, Node, and Cloudflare Workers without
 * any platform glue. (`font-utils.js` is pure on the same terms.)
 */

import { isSystemFontFamily } from './font-utils.js';

/**
 * Full font catalog, grouped by classification. Order within each group is
 * the order the dropdown shows them. Mirrors Pagy's catalog.
 */
export const FONT_GROUPS = {
  'Sans Serif': [
    'Albert Sans',
    'Amulya',
    'Archivo Narrow',
    'Archivo',
    'Bespoke Sans',
    'Bevellier',
    'Bricolage Grotesque',
    'Clash Display',
    'Clash Grotesk',
    'DM Sans',
    'Figtree',
    'Fira Sans',
    'Geist',
    'General Sans',
    'Grandstander',
    'iA Writer Quattro',
    'IBM Plex Sans',
    'Instrument Sans',
    'Inter',
    'Karla',
    'Lato',
    'Lexend',
    'Libre Franklin',
    'Manrope',
    'Merriweather Sans',
    'Mona Sans',
    'Montserrat',
    'Nunito Sans',
    'Nunito',
    'Open Sans',
    'Oswald',
    'Outfit',
    'Pilcrow Rounded',
    'Playpen Sans',
    'Plein',
    'Plus Jakarta Sans',
    'Poppins',
    'Public Sans',
    'Quicksand',
    'Raleway',
    'Ranade',
    'Roboto',
    'Rubik',
    'Satoshi',
    'Shantell Sans',
    'Sora',
    'Source Sans',
    'Space Grotesk',
    'Spline Sans',
    'Supreme',
    'Switzer',
    'Syne',
    'Work Sans'
  ],
  Serif: [
    'Alegreya',
    'Besley',
    'Bespoke Serif',
    'Bespoke Slab',
    'Bitter',
    'Boska',
    'Chubbo',
    'Crimson Pro',
    'EB Garamond',
    'Fraunces',
    'Gambetta',
    'IBM Plex Serif',
    'Instrument Serif',
    'Libre Caslon Condensed',
    'Lora',
    'Merriweather',
    'Playfair Display',
    'Recia',
    'Roboto Serif',
    'Roboto Slab',
    'Sentient',
    'Source Serif'
  ],
  Monospace: [
    'Departure Mono',
    'Geist Mono',
    'iA Writer Mono',
    'IBM Plex Mono',
    'Inconsolata',
    'Iosevka',
    'Jet Brains Mono',
    'Roboto Mono',
    'Source Code Pro'
  ]
};

/** Flat list of every font name, in display order across all groups. */
export const FONT_OPTIONS = [
  ...FONT_GROUPS['Sans Serif'],
  ...FONT_GROUPS.Serif,
  ...FONT_GROUPS.Monospace
];

/**
 * Source registry — tells the loader where to fetch each font's stylesheet.
 *  - 'google'    → fonts.googleapis.com/css2
 *  - 'fontshare' → api.fontshare.com/v2/css (Indian Type Foundry)
 *  - 'system'    → no remote load; falls back to generic family
 *
 * Anything not in this map defaults to 'google'.
 */
export const FONT_SOURCES = {
  // Fontshare (Indian Type Foundry)
  Amulya: 'fontshare',
  'Bespoke Sans': 'fontshare',
  'Bespoke Serif': 'fontshare',
  'Bespoke Slab': 'fontshare',
  Bevellier: 'fontshare',
  Boska: 'fontshare',
  Chubbo: 'fontshare',
  'Clash Display': 'fontshare',
  'Clash Grotesk': 'fontshare',
  'Departure Mono': 'fontshare',
  Gambetta: 'fontshare',
  'General Sans': 'fontshare',
  'Pilcrow Rounded': 'fontshare',
  Plein: 'fontshare',
  Ranade: 'fontshare',
  Recia: 'fontshare',
  Satoshi: 'fontshare',
  Sentient: 'fontshare',
  Supreme: 'fontshare',
  Switzer: 'fontshare',
  // iA writer + Iosevka have no live CDN here.
  'iA Writer Quattro': 'system',
  'iA Writer Mono': 'system',
  Iosevka: 'system',
  // Jet Brains Mono *is* on Google (as "JetBrains Mono", no space).
  'Jet Brains Mono': 'google'
};

/**
 * Google Fonts is fussy about the canonical name. Catalog names that differ
 * from Google's canonical form map through this table; everything else passes
 * through as-is.
 */
const GOOGLE_CANONICAL_OVERRIDES = {
  'Jet Brains Mono': 'JetBrains Mono',
  'Source Sans': 'Source Sans 3',
  'Source Serif': 'Source Serif 4'
};

const GENERIC_FAMILY_BY_GROUP = {
  'Sans Serif': 'sans-serif',
  Serif: 'serif',
  Monospace: 'monospace'
};

const _groupForFamily = (() => {
  const m = new Map();
  for (const [group, names] of Object.entries(FONT_GROUPS)) {
    for (const n of names) m.set(n, group);
  }
  return m;
})();

const RECOMMENDED_FONTS = [
  'Inter',
  'Manrope',
  'General Sans',
  'Playfair Display',
  'Fraunces',
  'Geist'
];
export { RECOMMENDED_FONTS };

/**
 * Lowercase kebab slug used by the `pb-font-<slug>` inline-font class system
 * AND by the format engine's selection-time class swap. Pure function so the
 * sanitizer's class allow-list, the runtime CSS rule emitter, and the bridge
 * allow-list all agree on the same value.
 *
 * "Playfair Display" → "playfair-display", "iA Writer Quattro" → "ia-writer-quattro".
 */
export function slugifyFontName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const _bySlug = (() => {
  const m = new Map();
  for (const name of FONT_OPTIONS) m.set(slugifyFontName(name), name);
  return m;
})();

/** Returns the original family name for a slug, or null if unknown. */
export function findFontBySlug(slug) {
  if (!slug) return null;
  return _bySlug.get(String(slug).toLowerCase()) || null;
}

/**
 * Returns 'google' | 'fontshare' | 'system' for a family. Unknown → 'google',
 * EXCEPT for OS-installed families (Menlo, Georgia, Segoe UI…), which resolve
 * to 'system' so no loader asks a CDN for a font no CDN publishes. Every
 * consumer — the UI loader, the iframe <link> emission, the bridge live-inject
 * and the export fetcher — asks this one function, so the exclusion holds
 * everywhere rather than at whichever call site remembered it.
 */
export function getFontSource(familyName) {
  if (!familyName) return 'google';
  const declared = FONT_SOURCES[familyName];
  if (declared) return declared;
  return isSystemFontFamily(familyName) ? 'system' : 'google';
}

/** Returns Google's canonical family name (with overrides applied). */
export function getGoogleCanonicalName(familyName) {
  return GOOGLE_CANONICAL_OVERRIDES[familyName] || familyName;
}

/** Returns the generic family fallback for a font. */
export function genericFallbackFamily(familyName) {
  const group = _groupForFamily.get(familyName) || 'Sans Serif';
  return GENERIC_FAMILY_BY_GROUP[group];
}

const DEFAULT_WEIGHTS_QS = '300;400;500;600;700;800;900';

/** Build the GFonts CSS2 URL for a family. */
export function buildGoogleHotlinkHref(familyName, { weights } = {}) {
  const canonical = getGoogleCanonicalName(familyName);
  const wq = weights && weights.length ? weights.join(';') : DEFAULT_WEIGHTS_QS;
  return (
    'https://fonts.googleapis.com/css2?family=' +
    encodeURIComponent(canonical) +
    ':wght@' +
    wq +
    '&display=swap'
  );
}

/** Build the Fontshare CSS endpoint URL for a family. */
export function buildFontshareHotlinkHref(familyName, { weights } = {}) {
  const slug = slugifyFontName(familyName);
  const wq = weights && weights.length ? weights.join(',') : '300,400,500,600,700,800,900';
  return 'https://api.fontshare.com/v2/css?f%5B%5D=' + slug + '@' + wq + '&display=swap';
}

/**
 * Returns the loader payload for a family — `{ href, source }` for fonts that
 * need a remote stylesheet, or `null` for system fonts. Used by both the
 * iframe `<link>` emission and the bridge's live-inject path.
 */
export function buildLoaderTagsForFamily(familyName, opts = {}) {
  const source = getFontSource(familyName);
  if (source === 'system') return null;
  if (source === 'fontshare') {
    return { source, href: buildFontshareHotlinkHref(familyName, opts) };
  }
  return { source: 'google', href: buildGoogleHotlinkHref(familyName, opts) };
}

/**
 * Walk every section HTML in a state object and return the unique family
 * names referenced by inline `pb-font-<slug>` classes.
 *
 * Regex-based on purpose: scanning runs on every full render and export, and
 * a DOMParser walk over every section would be a noticeable cost on large
 * projects. The regex matches the builder's serialization shape (DOMParser
 * round-trips emit double-quoted class attributes; no template generator emits
 * single-quoted classes). If a future code path needs single-quote support,
 * swap to a DOM walker.
 */
export function collectInlineFontFamilies(state) {
  if (!state || typeof state !== 'object') return [];
  const slugs = new Set();
  const re = /class\s*=\s*"[^"]*\bpb-font-([a-z0-9-]+)\b[^"]*"/g;

  const addFromHtml = (html) => {
    if (typeof html !== 'string' || !html) return;
    let m;
    while ((m = re.exec(html)) !== null) {
      slugs.add(m[1]);
    }
  };

  const shared = state.sharedSections;
  if (shared && typeof shared === 'object') {
    addFromHtml(shared.navigation?.html);
    addFromHtml(shared.footer?.html);
  }

  if (Array.isArray(state.pages)) {
    for (const page of state.pages) {
      if (!page || !Array.isArray(page.sections)) continue;
      for (const section of page.sections) {
        addFromHtml(section?.html);
      }
    }
  }

  const out = [];
  for (const slug of slugs) {
    const family = findFontBySlug(slug);
    if (family && !out.includes(family)) out.push(family);
  }
  return out;
}

/**
 * Collect numeric font weights used by element-level overrides. The result is
 * deliberately conservative: an element can inherit a body, heading,
 * template, or nested inline family, so export/preview loaders request these
 * weights for every family they already need instead of risking faux weight
 * synthesis for an unresolved inheritance path.
 */
export function collectElementFontWeights(state) {
  if (!state || typeof state !== 'object') return [];
  const weights = new Set();

  const addSection = (section) => {
    const elements = section?.overrides?.elements;
    if (!elements || typeof elements !== 'object') return;
    for (const value of Object.values(elements)) {
      const raw = String(value?.fontWeight ?? '')
        .trim()
        .toLowerCase();
      const normalized = raw === 'normal' ? '400' : raw === 'bold' ? '700' : raw;
      if (/^[1-9]00$/.test(normalized)) weights.add(normalized);
    }
  };

  addSection(state.sharedSections?.navigation);
  addSection(state.sharedSections?.footer);
  for (const page of state.pages || []) {
    for (const section of page?.sections || []) addSection(section);
  }
  return Array.from(weights);
}
